import React from "react"
import styled from "styled-components"
import { Controller } from "react-hook-form"
import { InputLedyerConfig, LedyerConfig } from "lib/types/generated/graphql-types"
import { Common } from "lib/types"
import { SidebarDivider } from "../../DashboardSales/Shared.styled"
import Input from "components/Ui/Form/Input"
import SingleSelect from "components/Ui/Form/SingleSelect"
import { Control, UseFormSetValue } from "react-hook-form/dist/types/form"
import { FieldErrors } from "react-hook-form/dist/types/errors"
import { WithAuth } from "../../Auth"
import { PermissionGroups } from "../../../routes/constants"
import FallbackInput from "../../Ui/Form/FallbackInput"

const StyledSelect = styled(SingleSelect)`
  margin-bottom: 2rem;
`

interface ExtendedInputLedyerConfig {
  id: string
  ledyerConfig: InputLedyerConfig
}

type Props = {
  control: Control<ExtendedInputLedyerConfig>
  errors: FieldErrors<ExtendedInputLedyerConfig>
  setValue: UseFormSetValue<ExtendedInputLedyerConfig>
  showId?: boolean
  defaultValues?: LedyerConfig
}

export const LedyerConfigInputs = ({
  control,
  errors,
  setValue,
  showId = true,
  defaultValues
}: Props) => {
  const updateBaseUrl = (baseUrl: Common.Option) => {
    setValue("ledyerConfig.baseUrl", baseUrl.value)
  }

  const updateAuthUrl = (authUrl: Common.Option) => {
    setValue("ledyerConfig.authUrl", authUrl.value)
  }

  const baseUrlOptions = [
    {
      value: "https://api.sandbox.ledyer.com",
      label: "Test | https://api.sandbox.ledyer.com"
    },
    {
      value: "https://api.live.ledyer.com",
      label: "Production | https://api.live.ledyer.com"
    }
  ]

  const authUrlOptions = [
    {
      value: "https://auth.sandbox.ledyer.com/oauth/token",
      label: "Test | https://auth.sandbox.ledyer.com/oauth/token"
    },
    {
      value: "https://auth.live.ledyer.com/oauth/token",
      label: "Production | https://auth.live.ledyer.com/oauth/token"
    }
  ]

  return (
    <>
      {showId && (
        <>
          <Controller
            name="id"
            render={({ field }) => (
              <Input
                {...field}
                type="text"
                label="Identifier *"
                description="For internal use in Brink"
                placeholder="Aa - Zz, 0 - 9, -_."
                errors={errors}
              />
            )}
            control={control}
            defaultValue=""
            rules={{
              required: "This is a required field",
              pattern: {
                value: /^[A-Za-z0-9_.-]+$/,
                message: "Only characters Aa-Zz and 0-9 are allowed. No spaces"
              }
            }}
          />
          <SidebarDivider />
        </>
      )}
      <WithAuth
        requiredGroups={PermissionGroups.providers.write}
        fallback={<FallbackInput label={"Base URL"} value={defaultValues?.baseUrl} />}
      >
        <Controller
          name="ledyerConfig.baseUrl"
          render={({ field }) => (
            <StyledSelect
              {...field}
              options={baseUrlOptions}
              setValue={updateBaseUrl}
              defaultValue={{
                label:
                  baseUrlOptions.find((option) => {
                    return option.value === defaultValues?.baseUrl?.replace(/\/$/, "")
                  })?.label ?? "Select base URL",
                value: defaultValues?.baseUrl ?? ""
              }}
              label="Base URL *"
            />
          )}
          control={control}
          rules={{
            required: "This is a required field"
          }}
        />
      </WithAuth>
      <WithAuth
        requiredGroups={PermissionGroups.providers.write}
        fallback={<FallbackInput label={"Auth URL"} value={defaultValues?.authUrl} />}
      >
        <Controller
          name="ledyerConfig.authUrl"
          render={({ field }) => (
            <StyledSelect
              {...field}
              options={authUrlOptions}
              setValue={updateAuthUrl}
              defaultValue={{
                label:
                  authUrlOptions.find((option) => {
                    return option.value === defaultValues?.authUrl?.replace(/\/$/, "")
                  })?.label ?? "Select auth URL",
                value: defaultValues?.authUrl ?? ""
              }}
              label="Auth URL *"
            />
          )}
          control={control}
          rules={{
            required: "This is a required field"
          }}
        />
      </WithAuth>
      <WithAuth requiredGroups={PermissionGroups.providers.write}>
        <Controller
          name="ledyerConfig.clientId"
          render={({ field }) => (
            <Input {...field} type="text" errors={errors} label="Ledyer client id *" />
          )}
          control={control}
          rules={{
            required: "This is a required field"
          }}
          defaultValue=""
        />
        <Controller
          name="ledyerConfig.clientSecret"
          render={({ field }) => (
            <Input {...field} type="text" errors={errors} label="Ledyer client secret *" />
          )}
          control={control}
          rules={{
            required: "This is a required field"
          }}
          defaultValue=""
        />
      </WithAuth>
    </>
  )
}
